import type { SyntheticEvent } from 'react'
import type { useSortable } from '@dnd-kit/sortable'
import { GripIcon } from '../icons/icons'
import { Tooltip } from './Tooltip'

type SortableReturn = ReturnType<typeof useSortable>

interface DragHandleProps {
  listeners?: SortableReturn['listeners']
  attributes?: SortableReturn['attributes']
  className?: string
  disabled?: boolean
  /** Why the handle can't drag; its tooltip while `disabled`. */
  disabledReason?: string
  /** The handle's tooltip, and its name while it can drag. */
  tooltip?: string
  onGrabStart?: () => void
  /** The grip's size; 16 unless it stands in for a smaller icon. */
  iconSize?: number
}

export function DragHandle({
  listeners,
  attributes,
  className,
  disabled = false,
  disabledReason = 'At least one pinned label column is required',
  tooltip = 'Drag to reorder',
  onGrabStart,
  iconSize = 16,
}: DragHandleProps) {
  const handlePointerDown = (event: SyntheticEvent) => {
    onGrabStart?.()
    listeners?.onPointerDown?.(event)
  }

  return (
    <Tooltip
      variant="label"
      content={disabled ? disabledReason : tooltip}
      // A disabled handle leaves the tab order, so its reason takes focus.
      focusable={disabled}
      className="shrink-0"
    >
      <div
        className={`flex items-center ${
          disabled
            ? 'cursor-not-allowed text-faintest'
            : 'cursor-grab text-faint transition-colors hover:text-data active:cursor-grabbing'
        } ${className ?? ''}`}
        {...(disabled ? {} : listeners)}
        {...(disabled ? {} : { onPointerDown: handlePointerDown })}
        {...(disabled ? {} : attributes)}
        // The sortable attributes make it a button; it's named as its tooltip.
        aria-label={!disabled && attributes ? tooltip : undefined}
      >
        <GripIcon size={iconSize} aria-hidden="true" />
      </div>
    </Tooltip>
  )
}
